
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts/core';
import {
	TooltipComponent,
	TooltipComponentOption,
	ToolboxComponent,
	ToolboxComponentOption,
	GridComponent,
	GridComponentOption,
	LegendComponent,
	LegendComponentOption,
	MarkLineComponent,
	MarkLineComponentOption,
	DataZoomSliderComponent,
	DataZoomComponentOption,
} from 'echarts/components';
import {BarChart, BarSeriesOption, LineChart} from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { GobiColor } from '@/helpers/color';
import resize from 'vue-resize-directive';
import uuidv1 from 'uuid/v1';
import { DataZoomList } from '@/components/reports-v3/components/elements/charts/helpers/dataZoom';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import { DataViewFormatter } from './helpers/dataViewFormatter';

echarts.use([
	TooltipComponent,
	GridComponent,
	LegendComponent,
	MarkLineComponent,
	BarChart,
    LineChart,
	CanvasRenderer,
	DataZoomSliderComponent,
	ToolboxComponent,
]);

type EChartsOption = echarts.ComposeOption<
	| TooltipComponentOption
	| GridComponentOption
	| LegendComponentOption
	| MarkLineComponentOption
	| BarSeriesOption
	| DataZoomComponentOption
	| ToolboxComponentOption
>;

@Component({
	directives: {
		resize,
	},
})
export default class StackedBarChart extends Vue {
	// y-Axis
	@Prop({ default: () => [] }) public readonly yAxisData!: string[];
	@Prop({ default: '' }) public readonly yAxisLabel!: string;
	@Prop({ default: '' }) public readonly yAxisType!: any;
	@Prop({}) public readonly yAxisMin?: number;
	@Prop({}) public readonly yAxisMax?: number;
	// x-Axis
	@Prop({ default: () => [] }) public readonly xAxisData!: string[];
	@Prop({ default: '' }) public readonly xAxisLabel!: string;
	@Prop({ default: '' }) public readonly xAxisType!: any;
	@Prop({ default: 15 }) public readonly xAxisRotate!: number;
	@Prop({ default: 15 }) public readonly yAxisRotate!: number;
	@Prop({}) public readonly xAxisMin?: number;
	@Prop({}) public readonly xAxisMax?: number;
	// Formatter
	@Prop({ default: () => TooltipFormatter.basic })
	public readonly formatter!: any;
	@Prop({ default: () => DataViewFormatter.basic })
	public readonly dataViewFormatter!: any;
	// Other
	@Prop({
		default: () => [
			GobiColor.COLOR1,
			GobiColor.COLOR2,
			GobiColor.COLOR3,
			GobiColor.COLOR4,
			GobiColor.COLOR5,
			GobiColor.COLOR6,
		],
	})
	public readonly color!: string[];
	@Prop({ default: () => DataZoomList.xAxis('slider', 5, 0) })
	public readonly dataZoom!: echarts.EChartsCoreOption;
	@Prop({}) public readonly legends!: echarts.EChartsCoreOption;
	@Prop({ default: 'Title' }) public readonly title!: string;
	@Prop({ default: false }) public readonly isHorizontal!: boolean;
	@Prop({ default: () => [] })
	public readonly series!: EChartsOption;

	public mainChart: echarts.ECharts | null = null;
	public elId: string = '';

	@Watch('xAxisData')
	public onXAxisDataChange(newVal, oldVal) {
		this.updateChart();
	}

	@Watch('series')
	public onSeriesChange(newVal, oldVal) {
		this.updateChart();
	}

	public created() {
		this.elId = uuidv1();
	}

	public mounted() {
		this.initChart();
	}

	public initChart() {
		const myChart: any = document.getElementById(this.elId);

		const mainChart = (this.mainChart = echarts.init(myChart));
		this.updateChart();
	}

	public updateChart() {
		if (!this.mainChart) {
			return;
		}
		const option: echarts.EChartsCoreOption = {
			darkMode: 'auto',
			color: this.color,
			tooltip: {
				confine: true,
				trigger: 'axis',
				axisPointer: {
					type: 'shadow',
				},
				formatter: this.formatter,
			},
			legend: {
				type: 'scroll',
				selectedMode: true,
				data: this.legends,
				padding: [0, 180, 0, 0],
			},
			toolbox: {
				feature: {
					saveAsImage: {
						name: this.title,
						title: 'Save as image',
						iconStyle: {
							borderColor: GobiColor.COLOR1,
						},
					},
					dataView: {
						show: true,
						readOnly: true,
						title: 'Chart data',
						lang: ['', 'Close', ''],
						iconStyle: {
							borderColor: GobiColor.COLOR1,
						},
						optionToContent: this.dataViewFormatter,
					},
				},
			},
			grid: {
				top: this.legends && this.legends.length === 0 ? '10%' : '20%',
				bottom:
					this.dataZoom && this.dataZoom.type === 'slider' ? '20%' : '10%',
				left: `${3 + (this.isHorizontal && this.yAxisLabel ? 5 : 0)}%`,
				right: `${5 + (this.isHorizontal && this.yAxisLabel ? 5 : 0)}%`,
				containLabel: true,
			},
			dataZoom: this.dataZoom ? [this.dataZoom] : undefined,
			xAxis: {
				name: this.xAxisLabel,
				nameTextStyle: {
					fontWeight: 'bold',
				},
				type: this.xAxisType,
				boundaryGap: true,
				data: this.xAxisData,
				axisLabel: {
					rotate: this.xAxisRotate,
					fontSize: 9,
				},
				min: this.xAxisMin,
				max: this.xAxisMax,
			},
			yAxis: {
				name: this.yAxisLabel,
				nameTextStyle: {
					fontWeight: 'bold',
				},
				axisLabel: {
					rotate: this.yAxisRotate,
					fontSize: 9,
				},
				type: this.yAxisType,
				data: this.yAxisData,
				min: this.yAxisMin,
				max: this.yAxisMax,
			},
			stateAnimation: {
				duration: 300,
				easing: 'cubicOut',
			},
			easing: 'cubicOut',
			animation: true,
			animationDuration: 1000,
			animationDurationUpdate: 500,
			animationEasing: 'cubicInOut',
			animationEasingUpdate: 'cubicInOut',
			animationThreshold: 2000,
			progressiveThreshold: 3000,
			progressive: 400,
			hoverLayerThreshold: 3000,
			series: this.series,
		};
		if (this.isHorizontal) {
			const xAxis = option.xAxis as any;
			option.xAxis = option.yAxis as any;
			option.yAxis = xAxis;
		}
		this.mainChart.setOption(option, true);
	}

	public onResize() {
		if (this.mainChart) {
			this.mainChart.resize();
		}
	}
}
